<template>
  <AutoComplete
    v-model="localModel"
    v-bind="{
      disabled,
      placeholder,
      invalid
    }"
    completeOnFocus
    autoOptionFocus
    input-class="w-full"
    option-label="title"
    :multiple="multiple"
    :class="{ 'pointer-events-none': readonly }"
    :input-id="id"
    :suggestions="options"
    @blur="emits('blur', $event)"
    @focus="emits('focus', $event)"
    @complete="search"
  />
</template>

<script setup lang="ts">
import { defineModel, ref, watch, withDefaults } from 'vue'
import AutoComplete, { AutoCompleteCompleteEvent } from 'primevue/autocomplete'
import { Salespoint, useGetSalespointList, userIsAdmin } from '@/shared'
import { storeToRefs } from 'pinia'
import { User } from '@/entities/user'
import { useAuthModel } from '@/features/auth'

const props = withDefaults(
  defineProps<{
    id?: string
    disabled?: boolean
    placeholder?: string
    invalid?: boolean
    readonly?: boolean
    multiple?: boolean
    resolveModel?: boolean
  }>(),
  {
    multiple: false,
    resolveModel: true
  }
)

const emits = defineEmits<{
  blur: [Event]
  focus: [Event]
}>()

const model = defineModel<number | number[] | null>()

const localModel = ref<Salespoint | Salespoint[] | string | null>(null)
const options = ref<Salespoint[]>([])
const { post } = useGetSalespointList()
const { user } = storeToRefs(useAuthModel())

/** Простой кэш, чтобы не запрашивать одни и те же id повторно */
const cache = new Map<number, Salespoint>()

/** Ддобавление элементов в options без дублей */
function mergeOptions(items: Salespoint[]) {
  if (!items?.length) return
  const exists = new Set(options.value.map(o => o.id))
  items.forEach(it => {
    if (!exists.has(it.id)) options.value.push(it)
  })
}

/** Утилита: безопасно обновлять внешний v-model только если он реально изменился */
function setModelIfChanged(next: number | number[] | null) {
  const curr = model.value
  const same =
    Array.isArray(next) && Array.isArray(curr)
      ? next.length === curr.length && next.every((v, i) => v === curr[i])
      : next === curr
  if (!same) model.value = next
}

/** Ограничение видимых id для не-админа списком его ТТ */
function restrictIds(ids: number[]): number[] {
  const u = user.value as User | undefined
  if (!u || userIsAdmin(u) || !Array.isArray(u.salespoint_staff)) return ids
  const allow = new Set<number>(u.salespoint_staff)
  return ids.filter(id => allow.has(id))
}

/** Получить объекты ТТ по id, с использованием кэша */
async function getByIds(ids: number[]): Promise<Salespoint[]> {
  const filtered = restrictIds(ids)
  if (!filtered.length) return []

  const need = filtered.filter(id => !cache.has(id))
  if (need.length) {
    const res = await post({
      _page_size: need.length,
      _filter: { id: need } // бек должен трактовать массив как IN
    })
    const list = (res?._data as Salespoint[]) ?? []
    list.forEach(sp => cache.set(sp.id, sp))
  }

  return filtered.map(id => cache.get(id)).filter(Boolean) as Salespoint[]
}

/** Синхронизация: localModel -> model */
watch(localModel, v => {
  if (v == null) {
    setModelIfChanged(null)
    return
  }
  if (Array.isArray(v)) {
    setModelIfChanged(v.length ? v.map(s => s.id) : null)
  } else if (typeof v === 'string') {
    // свободный ввод — модель сбрасываем, пока не выбран объект
    setModelIfChanged(null)
  } else {
    setModelIfChanged(v.id)
  }
})

/** синхронизация model -> localModel  */
watch(
  model,
  async val => {
    if (props.resolveModel === false) return

    // пустое значение — очищаем внутреннюю модель
    if (val == null || (Array.isArray(val) && val.length === 0)) {
      localModel.value = props.multiple ? [] : null
      return
    }

    if (Array.isArray(val)) {
      const list = await getByIds(val)
      if (props.multiple) {
        localModel.value = list
        mergeOptions(list)
      } else {
        localModel.value = list[0] ?? null
        mergeOptions(list.slice(0, 1))
      }
      return
    }

    const list = await getByIds([val])
    const item = list[0]
    localModel.value = item ?? null
    if (item) mergeOptions([item])
  },
  { immediate: true }
)

/** Поиск по названию */
async function getList(query: string) {
  const res = await post({
    _page_size: 5,
    _filter: {
      title: `%${query?.trim()}%`,
      id: userIsAdmin(user.value as User) ? undefined : user.value?.salespoint_staff
    }
  })

  // Убираем уже выбранные при multiple
  const list = (res?._data || []).filter((sp: Salespoint) => {
    if (props.multiple && Array.isArray(localModel.value)) {
      return !localModel.value.find(sel => sel.id === sp.id)
    }
    return true
  })

  return list as Salespoint[]
}

async function search(event: AutoCompleteCompleteEvent) {
  options.value = await getList(event.query)
}
</script>
